"use client";
import React, { useState } from 'react';
import {
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
} from '@mui/material';
import DateRangeIcon from '@mui/icons-material/DateRange';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import GavelIcon from '@mui/icons-material/Gavel';
import CourtIcon from '@mui/icons-material/AccountBalance';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AnalysisIcon from '@mui/icons-material/BarChart';
import Pagination from '@mui/material/Pagination';
import Skeleton from '@mui/material/Skeleton';
import DownloadIcon from '@mui/icons-material/Download';

function Content({ searchResults, pagination, onPageChange }) {
  const [openDialog, setOpenDialog] = useState(false);
  const [highlightedText, setHighlightedText] = useState('');
  const [legalActsTreeData, setLegalActsTreeData] = useState([]);
  const [expandedItems, setExpandedItems] = useState({});
  const [loading, setLoading] = useState(false);

  const highlightTextWithLegalActs = (text, legalActs) => {
    const sortedActs = legalActs
      .filter((act) =>
        act.article_loc_start !== undefined &&
        act.article_loc_end !== undefined &&
        act.legal_act_name_start !== undefined &&
        act.legal_act_name_end !== undefined
      )
      .sort((a, b) => Math.min(a.legal_act_name_start, a.article_loc_start) - Math.min(b.legal_act_name_start, b.article_loc_start));

    let highlighted = '';
    let currentIndex = 0;

    sortedActs.forEach((act) => {
      const { legal_act_name_start, legal_act_name_end, article_loc_start, article_loc_end } = act;
      highlighted += text.slice(currentIndex, Math.min(legal_act_name_start, article_loc_start));

      if (legal_act_name_start !== undefined && legal_act_name_end !== undefined && currentIndex <= legal_act_name_start) {
        highlighted += `<span style="background-color: #FF8C00; color: white;">${text.slice(legal_act_name_start, legal_act_name_end)}</span>`;
        currentIndex = legal_act_name_end;
      }

      if (currentIndex < article_loc_start) {
        highlighted += text.slice(currentIndex, article_loc_start);
      }

      if (article_loc_start !== undefined && article_loc_end !== undefined && currentIndex <= article_loc_start) {
        highlighted += `<span style="background-color: #4CAF50; color: white;">${text.slice(article_loc_start, article_loc_end)}</span>`;
        currentIndex = article_loc_end;
      }
    });

    highlighted += text.slice(currentIndex);
    return highlighted;
  };

  const fetchDocumentData = async (documentId) => {
    setLoading(true);
    try {
      const response = await fetch('https://iids8vsrw0.execute-api.eu-north-1.amazonaws.com/test/get-document', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ document_id: documentId }),
      });
      const data = await response.json();

      if (data && data.decision_text) {
        const processedText = highlightTextWithLegalActs(data.decision_text, data.legal_acts);
        setHighlightedText(processedText);

        const legalActsMap = new Map();
        data.legal_acts.forEach((act) => {
          const title = act.title || 'Unnamed Act';
          const articleNumber = act.article_number;

          if (!legalActsMap.has(title)) {
            legalActsMap.set(title, new Map());
          }
          const articlesMap = legalActsMap.get(title);
          articlesMap.set(articleNumber, (articlesMap.get(articleNumber) || 0) + 1);
        });

        const treeData = Array.from(legalActsMap.entries()).map(([title, articlesMap]) => ({
          title,
          articles: Array.from(articlesMap.entries()).map(([articleNumber, count]) => ({
            articleNumber,
            count,
          })),
        }));
        setLegalActsTreeData(treeData);
      } else {
        setHighlightedText('No decision text available.');
        setLegalActsTreeData([]);
      }
    } catch (error) {
      console.error('Error fetching decision text:', error);
      setHighlightedText('Error fetching decision text.');
      setLegalActsTreeData([]);
    }
    setLoading(false);
  };

  const handleOpenDialog = (documentId) => {
    setOpenDialog(true);
    setHighlightedText('');
    setLegalActsTreeData([]);
    fetchDocumentData(documentId);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setHighlightedText('');
    setLegalActsTreeData([]);
  };

  const toggleExpand = (index) => {
    setExpandedItems((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  return (
    <>
      <Grid container spacing={2}>
        {searchResults.results.map((result, index) => (
          <Grid item xs={12} key={index} style={{ marginBottom: '2rem' }}>
            <Card style={{ backgroundColor: '#333333', position: 'relative' }}>
              <CardContent>
                <Box
                  style={{
                    marginBottom: '1rem',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: '1rem',
                  }}
                >
                  <Box style={{ display: 'flex', alignItems: 'center', fontSize: '0.7rem' }}>
                    <LocalOfferIcon style={{ fontSize: '1rem', marginRight: '0.3rem' }} />
                    <Typography variant="body2" color="text.secondary">
                      #{(pagination.page - 1) * 10 + index + 1}
                    </Typography>
                  </Box>
                  <Box style={{ display: 'flex', alignItems: 'center', fontSize: '0.8rem' }}>
                    <LocalOfferIcon style={{ fontSize: '1.2rem', marginRight: '0.5rem' }} />
                    <Typography variant="body2" color="text.secondary">
                      {result.ecli[0]?.case_number || '-'}
                    </Typography>
                  </Box>
                  <Box style={{ display: 'flex', alignItems: 'center', fontSize: '0.8rem' }}>
                    <DateRangeIcon style={{ fontSize: '1.2rem', marginRight: '0.5rem' }} />
                    <Typography variant="body2" color="text.secondary">
                      {result.ecli[0]?.doc_date || '-'}
                    </Typography>
                  </Box>
                  <Box style={{ display: 'flex', alignItems: 'center', fontSize: '0.8rem' }}>
                    <LocalOfferIcon style={{ fontSize: '1.2rem', marginRight: '0.5rem' }} />
                    <Typography variant="body2" color="text.secondary">
                      {result.ecli[0]?.seq_number || '-'}
                    </Typography>
                  </Box>
                  <Box style={{ display: 'flex', alignItems: 'center', fontSize: '0.8rem' }}>
                    <GavelIcon style={{ fontSize: '1.2rem', marginRight: '0.5rem' }} />
                    <Typography variant="body2" color="text.secondary">
                      {result.ecli[0]?.doc_type || '-'}
                    </Typography>
                  </Box>
                  <Box style={{ display: 'flex', alignItems: 'center', fontSize: '0.8rem' }}>
                    <CourtIcon style={{ fontSize: '1.2rem', marginRight: '0.5rem' }} />
                    <Typography variant="body2" color="text.secondary">
                      {result.ecli[0]?.court_code || '-'}
                    </Typography>
                  </Box>
                </Box>
                <Box style={{ marginBottom: '1rem' }}>
                  {result.top_themes.top1 && (
                    <Chip label={result.top_themes.top1} style={{ marginRight: '0.5rem' }} />
                  )}
                  {result.top_themes.top2 && (
                    <Chip label={result.top_themes.top2} style={{ marginRight: '0.5rem' }} />
                  )}
                  {result.top_themes.top3 && <Chip label={result.top_themes.top3} />}
                </Box>
                <SummaryComponent decisionSummary={result.decision_summary} />

                {/* Highlights */}
                <Typography variant="body2" color="text.secondary">
                  Highlights:
                </Typography>
                {result.highlights.map((highlight, highlightIndex) => (
                  <Typography
                    key={highlightIndex}
                    variant="body2"
                    color="text.secondary"
                    style={{ marginBottom: '0.5rem' }}
                    dangerouslySetInnerHTML={{
                      __html: highlight.replace(
                        /<em>(.*?)<\/em>/g,
                        '<em style="color: #81c784;">$1</em>'
                      ),
                    }}
                  ></Typography>
                ))}

                {/* "Analizēt" button */}
                <Box style={{ position: 'absolute', top: '1rem', right: '1rem', display: 'flex', gap: '1rem' }}>
  {/* "Analizēt" button */}
  <IconButton
    onClick={() => handleOpenDialog(result.id)}
    color="primary"
  >
    <AnalysisIcon />
    <Typography variant="body2" style={{ marginLeft: '0.3rem' }}>
      Analizēt
    </Typography>
  </IconButton>

  {/* Download button */}
  <a
  href={`https://manas.tiesas.lv/eTiesasMvc/nolemumi/pdf/${result.file_name.replace(/\.txt$/, '.pdf')}`}
  target="_blank"
  rel="noopener noreferrer"
  download
>
  <IconButton color="primary">
    <DownloadIcon />
    <Typography variant="body2" style={{ marginLeft: '0.3rem' }}>
      Lejupielādēt
    </Typography>
  </IconButton>
</a>
</Box>

                {/* Dialog for Analysis */}
                <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
                  <DialogTitle>Document Analysis</DialogTitle>
                  <DialogContent style={{ display: 'flex', gap: '2rem', alignItems: 'flex-start', maxHeight: '600px' }}>
                    {/* Analytics Block with Bar Charts */}
                    <Box
                      style={{
                        width: '350px',
                        maxHeight: '500px',
                        overflowY: 'auto',
                        paddingRight: '1rem',
                        borderRight: '1px solid #ccc',
                        flexShrink: 0,
                      }}
                    >
                      <Typography variant="h6" gutterBottom>Legal Acts & Articles</Typography>
                      {loading ? (
                        <>
                          <Skeleton variant="text" width="80%" height={20} />
                          <Skeleton variant="rectangular" width="100%" height={10} style={{ marginBottom: '1rem' }} />
                        </>
                      ) : (
                        <ul style={{ listStyle: 'none', padding: 0 }}>
                          {legalActsTreeData
                            .sort((a, b) => b.articles.length - a.articles.length)
                            .map((legalAct, index) => (
                              <li key={index} style={{ marginBottom: '1rem' }}>
                                {/* Bar chart for legal act count */}
                                <Box display="flex" alignItems="center" style={{ marginBottom: '4px' }}>
                                  <Box
                                    style={{
                                      width: `${(legalAct.articles.length / Math.max(...legalActsTreeData.map(act => act.articles.length))) * 100}%`,
                                      height: '10px',
                                      backgroundColor: '#ffb74d',
                                      marginRight: '8px',
                                    }}
                                  />
                                  <Typography variant="caption">{legalAct.articles.length}</Typography>
                                </Box>
                                <Box display="flex" alignItems="center">
                                  <IconButton onClick={() => toggleExpand(index)} size="small">
                                    {expandedItems[index] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                  </IconButton>
                                  <Typography variant="body2" style={{ flexGrow: 1 }}>{legalAct.title}</Typography>
                                </Box>
                                {expandedItems[index] && (
                                  <ul style={{ listStyle: 'none', paddingLeft: '1.5rem' }}>
                                    {legalAct.articles
                                      .sort((a, b) => b.count - a.count)
                                      .map((article, subIndex) => (
                                        <li key={subIndex} style={{ marginBottom: '8px' }}>
                                          <Box display="flex" alignItems="center" style={{ marginBottom: '4px' }}>
                                            <Box
                                              style={{
                                                width: `${(article.count / Math.max(...legalAct.articles.map(a => a.count))) * 100}%`,
                                                height: '10px',
                                                backgroundColor: '#81c784',
                                                marginRight: '8px',
                                              }}
                                            />
                                            <Typography variant="caption">{article.count}</Typography>
                                          </Box>
                                          <Typography variant="body2">{article.articleNumber}</Typography>
                                        </li>
                                      ))}
                                  </ul>
                                )}
                              </li>
                            ))}
                        </ul>
                      )}
                    </Box>

                    {/* Decision Text Block with Separate Scroll */}
                    <Box
                      style={{
                        flexGrow: 1,
                        maxHeight: '500px',
                        overflowY: 'auto',
                      }}
                    >
                      {loading ? (
                        <>
                          <Skeleton variant="text" width="100%" height={20} style={{ marginBottom: '0.5rem' }} />
                          <Skeleton variant="text" width="90%" height={20} style={{ marginBottom: '0.5rem' }} />
                        </>
                      ) : (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          paragraph
                          dangerouslySetInnerHTML={{ __html: highlightedText }}
                        ></Typography>
                      )}
                    </Box>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={handleCloseDialog} color="primary">Close</Button>
                  </DialogActions>
                </Dialog>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {pagination.totalPages > 1 && (
        <Box display="flex" justifyContent="center" marginTop={2}>
          <Pagination
            count={pagination.totalPages}
            page={pagination.page}
            onChange={(event, value) => onPageChange(value)}
            color="primary"
          />
        </Box>
      )}
    </>
  );
}

function SummaryComponent({ decisionSummary }) {
  const [expanded, setExpanded] = useState(false);

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  return (
    <Box
      style={{
        marginBottom: '1rem',
        backgroundColor: '#4f4f4f',
        padding: '1rem',
        borderRadius: '4px',
        position: 'relative',
      }}
    >
      <Typography variant="body2" color="text.secondary" style={{ marginBottom: '0.5rem' }}>
        {decisionSummary.description_summary}
      </Typography>
      {expanded && (
        <Box>
          <Typography variant="body2" color="text.secondary" style={{ marginBottom: '0.5rem' }}>
            {decisionSummary.motive_summary}
          </Typography>
          <Typography variant="body2" color="text.secondary" style={{ marginBottom: '0.5rem' }}>
            {decisionSummary.decision_summary}
          </Typography>
        </Box>
      )}
      <IconButton
        onClick={handleExpandClick}
        color="primary"
        size="small"
        style={{ position: 'absolute', bottom: '-20px', left: '50%', transform: 'translateX(-50%)' }}
      >
        <ExpandMoreIcon />
      </IconButton>
    </Box>
  );
}

export default Content;
